import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, ArrowRight, ShoppingBag, Users, Store, Star, MapPin, BadgeCheck, ChevronLeft, ChevronRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import VersionSwitcher from "@/components/VersionSwitcher";
import { slugForCategory } from "@/lib/categoryHeroes";
import { resolveAssetUrl } from "@/lib/assetUrl";

// Hero carousel images - Black professionals networking and working together
const HERO_IMAGES = [
  {
    url: "https://images.unsplash.com/photo-1739298061709-cfc57e6b620e?w=1200&q=80",
    caption: "Building Community Together",
    description: "Black entrepreneurs networking and collaborating"
  },
  {
    url: "https://images.unsplash.com/photo-1573165706511-3ffde6ef1fe3?w=1200&q=80",
    caption: "Women Leading the Way",
    description: "Black women business professionals in collaboration"
  },
  {
    url: "https://images.unsplash.com/photo-1716399233137-5f18a8e3520d?w=1200&q=80",
    caption: "Men Making Moves",
    description: "Black men entrepreneurs closing deals"
  },
  {
    url: `${process.env.REACT_APP_BACKEND_URL || ""}/api/uploads/categories/hero-dining.jpg`,
    caption: "Dining & Hospitality",
    description: "Black-owned fine dining and hospitality experiences"
  },
  {
    url: "https://images.unsplash.com/photo-1653566031535-bcf33e1c2893?w=1200&q=80",
    caption: "Collaborative Success",
    description: "Black professionals collaborating in business meeting"
  },
  {
    url: "https://images.unsplash.com/photo-1653565685060-e15e492a7fda?w=1200&q=80",
    caption: "Leading & Inspiring",
    description: "Black leaders presenting and inspiring teams"
  }
];

// Category images: centralised in /lib/categoryImages.js so the profile page
// and other consumers stay in sync. Re-imported here for the landing page UI.
import { CATEGORY_IMAGES } from "@/lib/categoryImages";
import CategoryRail from "@/components/CategoryRail";
import CommunityStats from "@/components/CommunityStats";

// Hero Carousel Component
const HeroCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + HERO_IMAGES.length) % HERO_IMAGES.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % HERO_IMAGES.length);
  };

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-tbc-gold/20 rounded-3xl transform rotate-3"></div>
      <div className="relative rounded-2xl overflow-hidden h-[400px] lg:h-[500px]">
        {HERO_IMAGES.map((image, index) => (
          <div
            key={image.url}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === currentIndex ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={image.url}
              alt={image.caption}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-tbc-navy/60 via-transparent to-transparent"></div>
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <h3 className="font-heading text-2xl mb-1">{image.caption}</h3>
              <p className="text-white/80 text-sm font-body">{image.description}</p>
            </div>
          </div>
        ))}
        
        {/* Navigation Arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          data-testid="carousel-prev"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          data-testid="carousel-next"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
        
        {/* Dots Indicator */}
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2">
          {HERO_IMAGES.map((image, index) => (
            <button
              key={`dot-${image.url}`}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex ? "bg-tbc-gold w-6" : "bg-white/50"
              }`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default function LandingPage() {
  const { user, login } = useAuth();
  const [categories, setCategories] = useState([]);
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [locationQuery, setLocationQuery] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catRes, bizRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/businesses?limit=6`)
      ]);
      setCategories(catRes.data.categories || []);
      setFeaturedBusinesses(bizRes.data.businesses || []);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Error fetching data:", error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set("search", searchQuery);
    if (locationQuery.trim()) params.set("location", locationQuery);
    window.location.href = `/directory?${params.toString()}`;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center">
        <div className="absolute inset-0 bg-tbc-cream grain-texture"></div>
        
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Text Content */}
            <div className="lg:col-span-6 space-y-8 animate-fadeIn">
              <div className="space-y-4">
                <span className="inline-block px-4 py-2 bg-tbc-gold/20 text-tbc-navy font-medium text-sm tracking-wide rounded-full">
                  BUILD BLACK WEALTH TOGETHER
                </span>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-tbc-navy tracking-tight leading-tight">
                  Discover & Support{" "}
                  <span className="text-tbc-gold">Black-Owned</span>{" "}
                  Businesses
                </h1>
                <p className="text-lg text-gray-600 font-body max-w-xl">
                  Join our community marketplace connecting you with authentic Black-owned businesses. 
                  Shop, support, and celebrate Black excellence.
                </p>
              </div>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="space-y-3 max-w-xl">
                <div className="flex items-center bg-white rounded-full shadow-lg border border-gray-100 overflow-hidden">
                  <Search className="w-5 h-5 text-gray-400 ml-4" />
                  <input
                    type="text"
                    placeholder="Search businesses or products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 px-4 py-4 text-gray-700 font-body focus:outline-none"
                    data-testid="hero-search-input"
                  />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1 flex items-center bg-white rounded-full shadow-lg border border-gray-100 overflow-hidden">
                    <MapPin className="w-5 h-5 text-gray-400 ml-4" />
                    <input
                      type="text"
                      placeholder="City, State, or ZIP..."
                      value={locationQuery}
                      onChange={(e) => setLocationQuery(e.target.value)}
                      className="flex-1 px-4 py-3 text-gray-700 font-body focus:outline-none"
                      data-testid="hero-location-input"
                    />
                  </div>
                  <Button 
                    type="submit"
                    className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-8 py-6 font-medium"
                    data-testid="hero-search-button"
                  >
                    Search
                  </Button>
                </div>
              </form>

              {/* Stats — live, with founding-member + 90-day goal framing */}
              <CommunityStats />
            </div>

            {/* Hero Carousel */}
            <div className="lg:col-span-6 animate-fadeIn stagger-2 opacity-0">
              <HeroCarousel />
            </div>
          </div>
        </div>
      </section>

      {/* Browse-by-category rail (horizontal carousel, all 18 categories) */}
      <CategoryRail />

      {/* Categories Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl text-tbc-navy mb-4">
              Explore Categories
            </h2>
            <p className="text-gray-600 font-body max-w-2xl mx-auto">
              Find exactly what you&apos;re looking for across our diverse range of Black-owned businesses
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.slice(0, 10).map((category, index) => {
              const heroSlug = slugForCategory(category);
              const to = heroSlug
                ? `/category/${heroSlug}`
                : `/directory?category=${encodeURIComponent(category)}`;
              return (
              <Link
                key={category}
                to={to}
                className={`group relative overflow-hidden rounded-xl aspect-square animate-fadeIn stagger-${(index % 5) + 1} opacity-0`}
                data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <img
                  src={CATEGORY_IMAGES[category] || CATEGORY_IMAGES["Other"]}
                  alt={category}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-tbc-navy/80 via-tbc-navy/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-white font-body font-medium text-sm tracking-wide">
                    {category}
                  </p>
                </div>
              </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Businesses */}
      <section className="py-20 bg-tbc-cream grain-texture">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl text-tbc-navy mb-2">
                Featured Businesses
              </h2>
              <p className="text-gray-600 font-body">
                Handpicked businesses making waves in our community
              </p>
            </div>
            <Link
              to="/directory"
              className="hidden sm:flex items-center gap-2 text-tbc-navy font-medium hover:text-tbc-gold transition-colors"
              data-testid="view-all-businesses"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredBusinesses.length > 0 ? (
              featuredBusinesses.map((business, index) => (
                <Link
                  key={business.business_id}
                  to={`/business/${business.business_id}`}
                  className={`group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 animate-fadeIn stagger-${(index % 3) + 1} opacity-0`}
                  data-testid={`business-card-${business.business_id}`}
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={resolveAssetUrl(business.image_url) || CATEGORY_IMAGES[business.category] || CATEGORY_IMAGES["Other"]}
                      alt={business.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-xs font-medium text-tbc-navy rounded-full">
                        {business.category === "Other" && business.custom_category 
                          ? business.custom_category 
                          : business.category}
                      </span>
                      {business.is_verified && (
                        <span className="px-2 py-1 bg-tbc-gold text-tbc-navy text-xs font-medium rounded-full flex items-center gap-1">
                          <BadgeCheck className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-xl text-tbc-navy mb-1 group-hover:text-tbc-gold transition-colors">
                      {business.name}
                    </h3>
                    {/* BC Score */}
                    {business.bc_score > 0 && (
                      <div className="flex items-center gap-1 mb-2">
                        {Array.from({ length: 5 }, (_, i) => (
                          <Star
                            key={`star-${i}`}
                            className={`w-3 h-3 ${
                              i < Math.floor(business.bc_score)
                                ? "fill-tbc-gold text-tbc-gold"
                                : "text-gray-300"
                            }`}
                          />
                        ))}
                        <span className="text-xs text-gray-500 ml-1">
                          {business.bc_score}/5
                        </span>
                      </div>
                    )}
                    <p className="text-gray-600 text-sm font-body line-clamp-2">
                      {business.description}
                    </p>
                    {business.city && business.state && (
                      <p className="text-gray-400 text-xs mt-3 font-body flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        {business.city}, {business.state}
                      </p>
                    )}
                  </div>
                </Link>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-body">No businesses yet. Be the first to register!</p>
                {!user && (
                  <Button
                    onClick={login}
                    className="mt-4 bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-8 py-6"
                    data-testid="register-first-business"
                  >
                    Register Your Business
                  </Button>
                )}
              </div>
            )}
          </div>

          <Link
            to="/directory"
            className="sm:hidden flex items-center justify-center gap-2 text-tbc-navy font-medium hover:text-tbc-gold transition-colors mt-8"
          >
            View All Businesses <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-tbc-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">
            Own a Black Business?
          </h2>
          <p className="text-gray-300 font-body text-lg mb-8 max-w-2xl mx-auto">
            Join The Blk Community and connect with thousands of customers who want to support Black-owned businesses.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {user ? (
              <Link to="/dashboard">
                <Button
                  className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full px-8 py-6 font-medium text-lg"
                  data-testid="list-business-cta"
                >
                  List Your Business
                </Button>
              </Link>
            ) : (
              <Button
                onClick={login}
                className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full px-8 py-6 font-medium text-lg"
                data-testid="get-started-cta"
              >
                Get Started Free
              </Button>
            )}
            <Link to="/directory">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-tbc-navy rounded-full px-8 py-6 font-medium text-lg"
                data-testid="browse-directory-cta"
              >
                Browse Directory
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-tbc-navy mb-4">
              Why The Blk Community?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-tbc-cream/50 animate-fadeIn stagger-1 opacity-0">
              <div className="w-16 h-16 bg-tbc-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Store className="w-8 h-8 text-tbc-gold" />
              </div>
              <h3 className="font-heading text-xl text-tbc-navy mb-3">For Business Owners</h3>
              <p className="text-gray-600 font-body">
                Easily list your business, manage products, and reach customers who want to build Black wealth together.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-tbc-cream/50 animate-fadeIn stagger-2 opacity-0">
              <div className="w-16 h-16 bg-tbc-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <ShoppingBag className="w-8 h-8 text-tbc-gold" />
              </div>
              <h3 className="font-heading text-xl text-tbc-navy mb-3">For Shoppers</h3>
              <p className="text-gray-600 font-body">
                Discover unique products from Black-owned businesses. Every purchase makes a difference.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-tbc-cream/50 animate-fadeIn stagger-3 opacity-0">
              <div className="w-16 h-16 bg-tbc-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-tbc-gold" />
              </div>
              <h3 className="font-heading text-xl text-tbc-navy mb-3">Community First</h3>
              <p className="text-gray-600 font-body">
                Building wealth within our community through supporting each other&apos;s businesses.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <VersionSwitcher />
    </div>
  );
}
